"use client";

import { DropdownMenue } from "./dropdown-menue";

// Lucide „chevron-down“ und „check“ als Inline-Linien (das Paket führt keine Symbol-Bibliothek als Abhängigkeit).
const ChevronDown = ({ className }: { className: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const Check = ({ className }: { className: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export interface AnsichtOption<T extends string> {
  wert: T;
  label: string;
  /** Symbol vor dem Namen (Lucide, vom Aufrufer; Größe `size-3.5`). */
  symbol?: React.ReactNode;
}

// Klassen aus dem Ansichts-Select der Leitvorlage (hs-select: toggleClasses, dropdownClasses, optionClasses).
const AUSLOESER =
  "py-1.5 px-2.5 inline-flex items-center gap-x-1.5 text-start text-foreground text-[13px] rounded-lg align-middle hover:bg-muted-hover focus:bg-muted-focus focus:outline-hidden disabled:opacity-50 disabled:pointer-events-none";
const FLAECHE = "fixed top-0 start-0 z-70 bg-select border border-select-line rounded-xl shadow-xl focus:outline-hidden";
const EINTRAG =
  "py-2 px-3 w-full flex items-center gap-x-2 text-sm text-start text-select-item-foreground cursor-pointer hover:bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus";
const EINTRAG_GEWAEHLT =
  "py-2 px-3 w-full flex items-center gap-x-2 text-sm text-start text-select-item-foreground cursor-pointer bg-select-item-active hover:bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus";

/**
 * Ansichtsumschalter (Blockwahl Kanban L6, Design-Guide `ansicht-wahl.md`): ein Knopf mit der gewählten Ansicht,
 * darunter die Liste der Ansichten (Einzelwahl). Kontrolliert: der Aufrufer hält den Wert (z. B. in der Adresse).
 * Der Baustein lädt nichts und prüft keine Rechte; Ansichten, die jemand nicht sehen darf, übergibt der Aufrufer nicht.
 */
export function AnsichtWahl<T extends string>({
  titel,
  optionen,
  wert,
  onWahl,
}: {
  /** Name der Wahl für Screenreader und Tooltip („Ansicht“). */
  titel: string;
  optionen: readonly AnsichtOption<T>[];
  wert: T;
  onWahl: (neu: T) => void;
}) {
  const aktuell = optionen.find((o) => o.wert === wert) ?? optionen[0];
  return (
    <DropdownMenue
      ausloeserKlasse={AUSLOESER}
      ausloeserLabel={`${titel}: ${aktuell?.label ?? ""}`}
      ausloeserTitel={titel}
      rechts={false}
      flaecheKlasse={FLAECHE}
      ausloeser={
        <>
          {aktuell?.symbol}
          {aktuell?.label}
          <ChevronDown className="shrink-0 size-3.5 text-muted-foreground-1" />
        </>
      }
    >
      {({ schliessen }) => (
        <>
          {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-select-menus/view-mode-select */}
          <div className="w-44 p-1 space-y-0.5">
            {optionen.map((o) => (
              <button
                key={o.wert}
                type="button"
                role="menuitemradio"
                aria-checked={o.wert === wert}
                className={o.wert === wert ? EINTRAG_GEWAEHLT : EINTRAG}
                onClick={() => {
                  onWahl(o.wert);
                  schliessen();
                }}
              >
                {o.symbol}
                <span className="grow truncate">{o.label}</span>
                {o.wert === wert && <Check className="shrink-0 size-3.5" />}
              </button>
            ))}
          </div>
        </>
      )}
    </DropdownMenue>
  );
}
